import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { Guid } from "guid-typescript";

// COMPONENTS
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import { KogTabsComponent } from "@/main/components/shared/tabs/tabs";

// SERVICES
import { bodyshopApi, supplierApi, supplyConfigurationApi } from "@/main/api";
import { BodyShopFilterRequest, SupplierSaveRequest } from "@/main/models/request";
import {
	getPhoneCountryCode,
	getTranslateByKey,
	isNullUndefined,
	isShowControlByElementId,
} from "@/main/services/utils";

// STYLES AND LOCAL COMPONENTS
import SectionInfo from "./sectionInfo";
import ContactInfo, {
	IContactInfoState,
	formattedContactGet,
	formattedContactSave,
} from "@/main/components/shared/contactInfo";
import AddressInfo, { IAddressInfoState } from "@/main/components/shared/addressInfo";
import WarehouseInfo, { IWarehouseInfoState, formattedWarehouseGet, formattedWarehouseSave } from "./warehouseInfo";
import SupplyConfig from "@/main/components/shared/supplyConfig";
import { ISupplierRegisterForm, SupplierRegisterProvider, useSupplierRegisterContext } from "./context";

import * as S from "./styles";
import { ParameterTypesResponse } from "@/main/models/response/parameterTypesResponse";
import { RoutesConstants } from "@/main/routes/routesConstants";
import SettingsSupplier from "./settingsSupplier/settingsSupplier";
import { IDataDeliveryLogistic } from "./deliveryLogistic/types";
import { EvaluationPartialScoreData, SupplyConfigurationResponse } from "@/main/models/response";
import { evaluateSupplierExitConfirmation, fillAssociatedBodyShopMessage } from "./associatedBodyShopUtils";
import { evaluateDuplicateRegistrationConfirmation } from "./registrationDuplicateUtils";
import { KeyValue } from "@/main/models/common";
import ScoreInfo from "./scoreInfo/scoreInfo";
import SupplierCompanyBuyerInfo, { ISupplierCompanyBuyerInfo } from "@/main/components/shared/supplierCompanyBuyer";
import { Controls } from "@/main/services/constants/constantsControl";
import { KOGAreaButtons } from "@/main/components/shared/styles";
import useAsyncModalConfirm from "@/main/components/shared/asyncModalConfirm/asyncModalConfirm";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { toBase64 } from "@/main/services/utilsFile";
import _ from "lodash";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import CompanyHistorical from "@/main/components/shared/companyHistorical/companyHistorical";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";

function SupplierRegisterScreen() {
	const INTIAL_TAB_REGISTRATION: number = 0;

	// Hooks
	const navigate = useNavigate();
	const methods = useFormContext<ISupplierRegisterForm>();
	const [searchParams, setSearchParams] = useSearchParams();
	const { isEdit, setIsEdit } = useSupplierRegisterContext();
	const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();
	const [getAssociatedBodyShopsConfirmation, ModalAssociatedBodyShopsConfirmation] = useAsyncModalConfirm();
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
	const guidSupplierParams = searchParams.get("supplierGuid");
	const watcherCountry = methods.watch("country");
	const watcherRfc = methods.watch("rfc");

	// States
	const [isLoadingInfo, setIsLoadingInfo] = useState(false);
	const [dataContacts, setDataContacts] = useState<IContactInfoState[]>([]);
	const [initialDataContacts, setInitialDataContacts] = useState<IContactInfoState[]>([]);
	const [dataAddress, setDataAddress] = useState<IAddressInfoState[]>([]);
	const [initialDataAddress, setInitialDataAddress] = useState<IAddressInfoState[]>([]);
	const [dataWarehouse, setDataWarehouse] = useState<IWarehouseInfoState[]>([]);
	const [initialDataWarehouse, setInitialDataWarehouse] = useState<IWarehouseInfoState[]>([]);
	const [dataDitribution, setDataDitribution] = useState<string[]>([]);
	const [dataScoreInfo, setDataScoreInfo] = useState<EvaluationPartialScoreData | null>(null);
	const [dataAssingnableBodyshop, setDataAssingnableBodyshop] = useState<KeyValue[]>([]);
	const [guidAssignedBodyShop, setGuidAssignedBodyShop] = useState<string | null>(null);
	const [initialGuidAssignedBodyShop] = useState<string | null>(null);
	const [supplyConfig, setSupplyConfig] = useState<SupplyConfigurationResponse[]>([]);
	const [dataParameterTypes, setDataParameterTypes] = useState<ParameterTypesResponse[]>([]);
	const [savedataDeliverLogistic, setSavedDataDeliverLogistic] = useState<IDataDeliveryLogistic[]>([]);
	const [savedDataDitribution, setSavedDataDitribution] = useState<string[]>([]);
	const [supplierCompanyBuyerGrid, setSupplierCompanyBuyerGrid] = useState<ISupplierCompanyBuyerInfo[]>([]);
	const [supplierCompanyBuyerDeleteItem, setSupplierCompanyBuyerDeleteItem] = useState<string[]>([]);

	const [guidCompany, setGuidCompany] = useState<string>();
	const [loadedStatusKey, setLoadedStatusKey] = useState<number | null>(null);
	const [loadedRegistrationNumber, setLoadedRegistrationNumber] = useState<string>("");

	//User Control
	const showSectionSupplierBuyer = isShowControlByElementId(Controls.IdSectionSupplierBuyer);

	// Methods & Variables for Form
	const disableCopyWarehouse = () => {
		const haveWarehouse = dataWarehouse.length > 0;
		const havePrimaryContact = dataContacts.find((contact) => contact.isPrimaryContact);
		const haveAddress = dataAddress.length > 0;

		return haveWarehouse || !havePrimaryContact || !haveAddress;
	};

	const isFormChanged = methods.formState.isDirty;
	const isContactsChanged = !_.isEqual(initialDataContacts, dataContacts);
	const isAddressChanged = !_.isEqual(initialDataAddress, dataAddress);
	const isWarehouseChanged = !_.isEqual(initialDataWarehouse, dataWarehouse);
	const isBodyshopChanged = initialGuidAssignedBodyShop !== guidAssignedBodyShop;
	const isSupplierCompanyBuyerChanged = supplierCompanyBuyerDeleteItem.length > 0;

	const hasChanges =
		isFormChanged ||
		isContactsChanged ||
		isAddressChanged ||
		isWarehouseChanged ||
		isBodyshopChanged ||
		isSupplierCompanyBuyerChanged;

	const formatDataAddress = (addresses: IAddressInfoState[], isEditAddress = false) => {
		return addresses.map((address) => ({
			...(isEditAddress && {
				guid: Guid.isGuid(address.id) ? address.id : Guid.EMPTY,
			}),
			idAddressType: Number(address.addressType.key),
			street: address.address,
			complement: address.complement,
			number: address.number,
			municipality: {
				guid: String(address.municipality.key),
			},
		}));
	};

	// HANDLE FUNCTIONS
	function validateForm() {
		const haveContacts = dataContacts.length > 0;
		const havePrimaryContact = dataContacts.find((contact) => contact.isPrimaryContact);
		const haveAddress = dataAddress.length > 0;
		const haveWarehouse = dataWarehouse.length > 0;
		const messages = [];

		if (!haveContacts) messages.push(getTranslateByKey("information.mustHaveAtLeastOneContact", true));
		if (haveContacts && !havePrimaryContact)
			messages.push(getTranslateByKey("information.mustHaveAtLeastOnePrimaryContact", true));
		if (!haveAddress) messages.push(getTranslateByKey("information.mustHaveAtLeastOneAddress", true));
		if (!haveWarehouse) messages.push(getTranslateByKey("information.mustHaveAtLeastWarehouse", true));

		if (messages.length > 0) {
			messages.forEach((message) => toast.error(message));
			return false;
		}
		return true;
	}
	function handleBack() {
		navigate(RoutesConstants.registrationSearchSupplier);
	}
	function handleCopyWarehouse() {
		const formattedContacts = formattedContactSave(dataContacts);

		const contactPrimary = formattedContacts.find((contact) => contact.principal);
		const [codePhone, phone] = contactPrimary?.phone.split(" ") ?? [];
		const codeCountry = getPhoneCountryCode();

		const findCodePhoneNumber = codeCountry.find((item) => item.key === codePhone) ?? { key: "", value: "" };

		if (!contactPrimary) return;
		const formattedWarehouse: IWarehouseInfoState[] = dataAddress.map((address) => ({
			id: uuidv4(),
			name: address.addressType.value,
			contactName: `${contactPrimary.firstName} ${contactPrimary.lastName}`,
			phoneNr: phone ?? contactPrimary.phone,
			extension: contactPrimary.extension,
			zipCode: address.zipCode,
			municipality: address.municipality,
			state: address.state,
			city: address.city,
			address: address.address,
			number: address.number,
			complement: address.complement,
			codePhoneNumber: findCodePhoneNumber,
			phoneIsWhatsApp: contactPrimary.phoneIsWhatsApp,
		}));

		setDataWarehouse(formattedWarehouse);
	}

	// Async Functions
	function loadInfoSupplier(guidSupplierRequest: string) {
		supplierApi
			.getSupplierData({ guid: guidSupplierRequest })
			.then((response) => {
				const resultData = response.data.result;

				// // Set Form values
				methods.reset({
					country: {
						key: resultData.country.guid,
						value: resultData.country.name,
					},
					rfc: resultData.registrationNumber,
					tradeName: resultData.tradeName,
					companyName: resultData.companyName,
					status: {
						key: resultData.statusRegister.idStatusRegister,
						value: resultData.statusRegister.name,
					},
				});
				methods.setValue("companyName", resultData.companyName);
				methods.setValue("rfc", resultData.registrationNumber);
				methods.setValue("status", {
					key: resultData.statusRegister.idStatusRegister,
					value: resultData.statusRegister.name,
				});
				methods.setValue("tradeName", resultData.tradeName);
				setLoadedStatusKey(Number(resultData.statusRegister.idStatusRegister));
				setLoadedRegistrationNumber(resultData.registrationNumber);
				if (resultData.supplierCategory) {
					methods.setValue("supplierCategory", resultData.supplierCategory);
				}

				const formattedContacts = resultData.contacts.map((contact) => formattedContactGet(contact));
				const contacts = formattedContacts ?? [];
				setDataContacts(_.cloneDeep(contacts));
				setInitialDataContacts(_.cloneDeep(contacts));

				const formattedAddress = resultData.addresses.map((address) => ({
					id: address.guid,
					address: address.street,
					complement: address.complement,
					number: address.number === "" ? null : address.number,
					zipCode: address.municipality.zipCode,
					addressType: {
						key: address.addressType.idAddressType,
						value: address.addressType.name,
					},
					city: {
						key: address.municipality.city.guid,
						value: address.municipality.city.name,
					},
					municipality: {
						key: address.municipality.guid,
						value: address.municipality.name,
					},
					state: {
						key: address.municipality.city.state.guid,
						value: address.municipality.city.state.name,
					},
				})) as IAddressInfoState[];
				const addresses = formattedAddress ?? [];
				setDataAddress(_.cloneDeep(addresses));
				setInitialDataAddress(_.cloneDeep(addresses));

				const formattedWarehouse = resultData.warehouses.map((warehouse) => formattedWarehouseGet(warehouse));
				const warehouses = formattedWarehouse ?? [];
				setDataWarehouse(_.cloneDeep(warehouses));
				setInitialDataWarehouse(_.cloneDeep(warehouses));

				const formattedSupplierCompanyBuyer = resultData.supplierCompanyBuyers.map((supplierCompanyBuyer) => ({
					guid: supplierCompanyBuyer.guid,
					buyerName: supplierCompanyBuyer.buyerName,
					userDateTime: supplierCompanyBuyer.userDateTime,
					companyTypeName: supplierCompanyBuyer.companyTypeName,
				})) as ISupplierCompanyBuyerInfo[];

				setSupplierCompanyBuyerGrid(formattedSupplierCompanyBuyer);

				const formattedDistribution = resultData.supplierDistributionRegions
					.map((distribution) => distribution?.guidDistributionRegion)
					.filter((guid) => guid != null);
				setSavedDataDitribution(formattedDistribution);
				setDataDitribution(formattedDistribution);

				const formattedDeliveryLogistic: IDataDeliveryLogistic[] = resultData.deliveryWithoutLogisticCompanys.map(
					(item) => ({
						id: item.guid,
						guid: item.guid,
						finalZipCode: item.zipCodeEnd,
						initialZipCode: item.zipCodeStart,
						deliveryType: item.deliveryType,
						idDeliveryType: item.idDeliveryType,
						daysDelivery: String(item.daysToDeliveryPart),
						concatString: `${item.zipCodeStart}${item.zipCodeEnd}${String(item.daysToDeliveryPart)}`,
					}),
				);
				setSavedDataDeliverLogistic(formattedDeliveryLogistic);
				setDataParameterTypes(resultData.parameterTypes);
				setDataScoreInfo(resultData.evaluationPartialScores);
				setGuidCompany(resultData.guidCompany);
			})
			.catch(() => {})
			.finally(() => {
				setIsLoadingInfo(false);
			});

		const requestForm = {
			guidSupplier: String(guidSupplierParams),
			guidCountry: String(watcherCountry?.key),
		};

		supplyConfigurationApi.getbysupplier(requestForm).then((response) => {
			setSupplyConfig(response.data.result);
		});
	}

	async function handleSubmitSaveSupplier(data: ISupplierRegisterForm) {
		if (!validateForm()) return;

		if (isEdit) {
			const isFormChanged = methods.formState.isDirty;
			const isContactsChanged = !_.isEqual(initialDataContacts, dataContacts);
			const isAddressChanged = !_.isEqual(initialDataAddress, dataAddress);
			const isWarehouseChanged = !_.isEqual(initialDataWarehouse, dataWarehouse);
			const isBodyshopChanged = initialGuidAssignedBodyShop !== guidAssignedBodyShop;
			const isSupplierCompanyBuyerChanged = supplierCompanyBuyerDeleteItem.length > 0;

			if (
				!isFormChanged &&
				!isContactsChanged &&
				!isAddressChanged &&
				!isWarehouseChanged &&
				!isBodyshopChanged &&
				!isSupplierCompanyBuyerChanged
			) {
				return;
			}
		}

		let filesRequest = undefined;
		let guidReason = null;
		let reasonDescription = null;

		if (isEdit) {
			const isExitConfirmed = await evaluateSupplierExitConfirmation(
				{
					loadedStatusKey,
					loadedRegistrationNumber,
					newStatusKey: Number(data.status?.key),
					newRegistrationNumber: data.rfc,
				},
				{
					fetchAssociatedBodyShops: async () => {
						const associatedResponse = await supplierApi.getAssociatedBodyShops(String(guidSupplierParams));
						return associatedResponse.data.result ?? [];
					},
					confirm: (associatedBodyShops) =>
						getAssociatedBodyShopsConfirmation(
							<>
								<strong>{getTranslateByKey("information.supplierLinkedToBodyshopTitle")}</strong>
								<br />
								{fillAssociatedBodyShopMessage(
									getTranslateByKey("information.supplierLinkedToBodyshopMessage"),
									associatedBodyShops,
								)}
							</>,
						),
				},
			);
			if (!isExitConfirmed) return;
		}

		const duplicateOutcome = await evaluateDuplicateRegistrationConfirmation(
			async () => {
				const checkResponse = await supplierApi.checkRegistrationDuplicates({
					guid: isEdit ? String(guidSupplierParams) : undefined,
					guidCountry: String(data.country?.key),
					registrationNumber: data.rfc,
					tradeName: data.tradeName,
				});
				return checkResponse.data.result;
			},
			() =>
				getConfirmation(
					getTranslateByKey("information.thereIsAlreadeyRfcCompanyName")
						.replace("{0}", data.tradeName)
						.replace("{1}", data.rfc),
				),
		);
		if (duplicateOutcome === "cancelled") return;
		const isOverrideCompanyNameRfc = duplicateOutcome === "confirmed";

		if (isEdit) {
			const resultConfirm = await getConfirmationReason(
				getTranslateByKey("information.confirmChangeTitle"),
				ReasonTypeEnum.ChangeCompany,
			);

			if (!resultConfirm.confirmed) return;

			if (resultConfirm.files && resultConfirm.files.length > 0) {
				filesRequest = await Promise.all(
					resultConfirm.files.map(async (item) => {
						const base64DataUrl = await toBase64(item);
						return {
							fileName: item.name,
							contentType: item.type,
							fileBase64: base64DataUrl as string,
						};
					}),
				);
			}
			guidReason = resultConfirm.reason ? String(resultConfirm.reason.key) : null;
			reasonDescription = resultConfirm.observation || null;
		}

		const formattedContacts = formattedContactSave(dataContacts, isEdit);
		const formattedAddress = formatDataAddress(dataAddress, isEdit);
		const formattedWarehouse = formattedWarehouseSave(dataWarehouse, isEdit);

		const requestData: SupplierSaveRequest = {
			...(isEdit && { guid: String(guidSupplierParams) }),
			supplierCompanyBuyers: supplierCompanyBuyerDeleteItem,
			guidCountry: String(data.country?.key),
			companyName: data.companyName,
			tradeName: data.tradeName,
			registrationNumber: data.rfc,
			idStatusRegister: Number(data.status?.key),
			guidBodyshop: guidAssignedBodyShop,
			guidSupplierCategory: String(data.supplierCategory?.key ?? ""),
			contacts: formattedContacts,
			addresses: formattedAddress,
			warehouses: formattedWarehouse,
			isOverrideCompanyNameRfc: Boolean(isOverrideCompanyNameRfc),
			...(isEdit && {
				changeJustification: {
					guidReason: guidReason,
					reasonDescription: reasonDescription,
					filesUpload: filesRequest,
				},
			}),
		};

		supplierApi
			.saveSupplier(requestData)
			.then((response) => {
				const { result } = response.data;
				const message = isEdit
					? getTranslateByKey("information.supplierEditSuccessfully")
					: getTranslateByKey("information.supplierSavedSuccessfully");
				toast.success(message);

				if (guidSupplierParams) {
					setIsLoadingInfo(true);
					loadInfoSupplier(guidSupplierParams);
				}

				if (!isEdit) {
					setIsEdit(true);
					setSearchParams({ supplierGuid: result.guid });
				}
			})
			.catch(() => {})
			.finally(() => {});
	}

	function loadBodyshopByRFC(rfc: string) {
		setGuidAssignedBodyShop(null);
		setDataAssingnableBodyshop([]);
		if (rfc.length < 12) return;
		supplierApi
			.getBodyshopByRFC(rfc, watcherCountry?.key as string)
			.then((response) => {
				const { result } = response.data;
				if (result) {
					const bodyshops = result.map((resultBodyshop) => {
						return {
							key: resultBodyshop.guidBodyshop,
							value: resultBodyshop.bodyshopName,
						};
					});
					setDataAssingnableBodyshop(bodyshops);
				}
			})
			.catch(() => {})
			.finally(() => {
				setIsLoadingInfo(false);
			});
	}

	function handleBodyshopAssignment(bodyshopGuid: string | null) {
		if (!bodyshopGuid) return;
		setGuidAssignedBodyShop(bodyshopGuid);
		if (bodyshopGuid === "00000000-0000-0000-0000-000000000000") return;
		setIsLoadingInfo(true);
		const request: BodyShopFilterRequest = {
			guidBodyShop: bodyshopGuid,
			guidCountry: String(watcherCountry?.key),
		};

		bodyshopApi
			.getRegistrationDataBodyshop(request)
			.then((response) => {
				const { result } = response.data;
				if (result) {
					const formattedContacts = result.contacts.map((contact) =>
						formattedContactGet(contact),
					) as IContactInfoState[];

					const formattedAddress = result.addresses.map((address) => ({
						id: address.guid,
						address: address.street,
						complement: address.complement,
						number: address.number === "" ? null : address.number,
						zipCode: address.municipality.zipCode,
						addressType: {
							key: address.addressType.idAddressType,
							value: address.addressType.name,
						},
						city: {
							key: address.municipality.city.guid,
							value: address.municipality.city.name,
						},
						municipality: {
							key: address.municipality.guid,
							value: address.municipality.name,
						},
						state: {
							key: address.municipality.city.state.guid,
							value: address.municipality.city.state.name,
						},
					})) as IAddressInfoState[];

					setDataContacts(formattedContacts);
					setDataAddress(formattedAddress);
				}
			})
			.catch(() => {})
			.finally(() => setIsLoadingInfo(false));
	}

	useEffect(() => {
		if (guidSupplierParams) {
			setIsEdit(true);
			setIsLoadingInfo(true);
			loadInfoSupplier(guidSupplierParams);
		}
	}, [guidSupplierParams]);

	if (isLoadingInfo) return <LoadingComponent />;

	function GeneralInfo() {
		return (
			<S.Container>
				<SectionInfo
					dataAssignableBodyshop={dataAssingnableBodyshop}
					setGuidAssignedBodyShop={setGuidAssignedBodyShop}
					handleBodyshopAssignment={handleBodyshopAssignment}
					watcherCountry={watcherCountry}
					watcherRfc={watcherRfc}
					loadBodyshopByRFC={loadBodyshopByRFC}
					guidAssignedBodyShop={guidAssignedBodyShop}
				/>
				<ScoreInfo dataScoreInfo={dataScoreInfo} />
				<ContactInfo
					gridId="id-registerSupplier-grid-contacts"
					dataContacts={dataContacts}
					setDataContacts={setDataContacts}
					watcherCountry={watcherCountry}
					companyType={CompanyTypeEnum.Supplier}
					companyName={methods.watch("companyName")}
				/>

				<AddressInfo
					gridId="id-registerSupplier-grid-addresses"
					disableDeliveryAddress
					watcherCountry={watcherCountry}
					dataAddress={dataAddress}
					setDataAddress={setDataAddress}
				/>
				<WarehouseInfo
					dataWarehouse={dataWarehouse}
					watcherCountry={watcherCountry}
					setDataWarehouse={setDataWarehouse}
					handleCopyWarehouse={handleCopyWarehouse}
					disableCopyWarehouse={disableCopyWarehouse()}
				/>
				{isEdit && !isNullUndefined(guidSupplierParams) && (
					<SupplyConfig
						guidSupplierParams={guidSupplierParams}
						supplyConfig={supplyConfig}
						setSupplyConfig={setSupplyConfig}
						watcherCountry={watcherCountry}
					/>
				)}
				{isEdit && showSectionSupplierBuyer && (
					<SupplierCompanyBuyerInfo
						watcherCountry={watcherCountry}
						guidSupplier={guidSupplierParams}
						supplierCompanyBuyerGrid={supplierCompanyBuyerGrid}
						setSupplierCompanyBuyerGrid={setSupplierCompanyBuyerGrid}
						setSupplierCompanyBuyerDeleteItem={setSupplierCompanyBuyerDeleteItem}
					/>
				)}

				<KOGAreaButtons $isRegisterScreen>
					<SecondaryButton type="button" onClick={handleBack} id="id-registerSupplier-button-back">
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<Button
						type="submit"
						disabled={!hasChanges}
						onClick={methods.handleSubmit((data) => handleSubmitSaveSupplier(data))}
						id="id-registerSupplier-button-save"
					>
						{getTranslateByKey("information.save")}
					</Button>
				</KOGAreaButtons>
				<ModalConfirmation />
				<ModalConfirmationReason />
				<ModalAssociatedBodyShopsConfirmation
					closeByOverlay={false}
					confirmLabelKey="information.continue"
					cancelLabelKey="information.cancel"
				/>
			</S.Container>
		);
	}

	const tabPanels = [
		{
			tab: getTranslateByKey("control.tabGeneralInfo"),
			panel: GeneralInfo(),
			isShowControl: true,
		},
		{
			tab: getTranslateByKey("control.tabSettings"),
			panel: (
				<SettingsSupplier
					dataDitribution={dataDitribution}
					watcherCountry={watcherCountry}
					parameterTypes={dataParameterTypes}
					savedDataDitribution={savedDataDitribution}
					savedDataDeliverLogistic={savedataDeliverLogistic}
					setDataDitribution={setDataDitribution}
					setParameterTypes={setDataParameterTypes}
					updateSavedDataDitribution={(data) => setSavedDataDitribution(data)}
					updateSavedDataDeliverLogistic={(data) => setSavedDataDeliverLogistic(data)}
				/>
			),
			isShowControl: isEdit && isShowControlByElementId("id-tab-settings"),
		},
		{
			tab: getTranslateByKey("control.historical"),
			panel: <CompanyHistorical guidCompany={guidCompany} />,
			isShowControl: isEdit && isShowControlByElementId(Controls.IdCompanyHistoricalSupplier),
		},
	];

	return (
		<ContainerScreen
			title={`
    ${getTranslateByKey("information.registration")}
    ${" > "}
    ${getTranslateByKey("information.registerSupplier")}

    `}
		>
			<KogTabsComponent tabPanels={tabPanels} tabCurrent={INTIAL_TAB_REGISTRATION} />
		</ContainerScreen>
	);
}
export default function SupplierContainer() {
	return (
		<SupplierRegisterProvider>
			<SupplierRegisterScreen />
		</SupplierRegisterProvider>
	);
}
